import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Play,
  Search,
  ThumbsUp,
  Trash2,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { formatFullDate } from "@/lib/format"
import {
  useDeleteVideo,
  useVideosList,
  type AdminVideo,
} from "@/lib/queries/videos"
import { ExportButton } from "@/components/ExportButton"
import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Videos (list)
// LEGACY: apps/admin/src/pages/Video.jsx
// API:    POST /admin/video_list   (list with server pagination/sort/search)
//         POST /admin/delete_video (delete)
//
// Edit/Upload pages are deferred to Batch D-edit (the upload pipeline is
// the heaviest flow in the admin and deserves its own batch).
//
// ─── Field parity (legacy ↔ v2) ──────────────────────────────────────
// LIST request body:
//   page          → page          (number)
//   limit         → limit         (number)
//   search        → search        (string)
//   sortyBy       → sortyBy       (string, backend typo PRESERVED)
//   sortDirection → sortDirection (1 | -1)
//
// LIST response columns rendered (same shape + same data as legacy):
//   narrated_by         → "Narrated By"        (string, sortable)
//   author              → "Author"             (string, sortable)
//   proverb             → "Proverb"            (string, sortable)
//   login_user_sequence → "Login User Sequence" (number, sortable)
//   skip_user_sequence  → "Skip User Sequence" (number, sortable)
//   like_count          → "👍 Like count"      (number, sortable)
//   purpose             → "Video Purpose"      (string, sortable)
//   thumb_url           → "Thumbnail"          (img)
//   video_url           → "Play"               (button → modal player)
//   created_at          → "Upload Date"        (date, sortable)
//   _id (action col)    → "Update" link         → /videos/:id (was /update_video/:id)
//   _id (action col)    → "Delete" button       → POST /admin/delete_video {id}
//
// DELETE confirm payload (POST /admin/delete_video):
//   id                  → id
//
// ─── Behavior parity ─────────────────────────────────────────────────
//   - Server pagination, server sort, server search (same params)
//   - Delete uses a confirmation Dialog (legacy used SweetAlert2; same
//     button labels: "Yes" → "Delete video", "Cancel" → "Keep video")
//   - Search is debounced 300ms (better than legacy's per-keystroke fire)
//   - Video player uses native <video controls> (legacy used react-player;
//     dropping the dep saves ~80KB; works for all the formats the app uses)
// ───────────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return v
}

export default function Videos() {
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounced(search, 300)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [sortyBy, setSortyBy] = useState<string | undefined>(undefined)
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1)

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, limit, sortyBy, sortDirection])

  const params = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || undefined,
      sortyBy,
      sortDirection,
    }),
    [page, limit, debouncedSearch, sortyBy, sortDirection]
  )

  const videosQ = useVideosList(params)
  const deleteMut = useDeleteVideo()

  const [playVideo, setPlayVideo] = useState<AdminVideo | null>(null)
  const [deleteVideo, setDeleteVideo] = useState<AdminVideo | null>(null)

  const onSortClick = (field: string) => {
    if (sortyBy === field) {
      setSortDirection((prev) => (prev === 1 ? -1 : 1))
    } else {
      setSortyBy(field)
      setSortDirection(-1)
    }
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortyBy !== field) return null
    return sortDirection === 1 ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    )
  }

  const totalPages = videosQ.data?.totalPages ?? 0
  const totalDocs = videosQ.data?.totalDocs ?? 0

  const handleDeleteConfirm = async () => {
    if (!deleteVideo) return
    await deleteMut.mutateAsync(deleteVideo._id)
    setDeleteVideo(null)
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-end sm:justify-between sm:space-y-0">
          <div>
            <CardTitle>Videos</CardTitle>
            <CardDescription>
              {totalDocs > 0
                ? `${totalDocs.toLocaleString()} total`
                : "All uploaded videos"}
            </CardDescription>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search videos…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                aria-label="Search videos"
              />
            </div>
            <ExportButton<AdminVideo>
              filenamePrefix="videos"
              totalEstimate={totalDocs}
              columns={[
                { label: "Narrated By", get: (v) => v.narrated_by },
                { label: "Author", get: (v) => v.author },
                { label: "Proverb", get: (v) => v.proverb },
                { label: "Purpose", get: (v) => v.purpose },
                { label: "Login User Sequence", get: (v) => v.login_user_sequence },
                { label: "Skip User Sequence", get: (v) => v.skip_user_sequence },
                { label: "Like Count", get: (v) => v.like_count ?? 0 },
                { label: "Share Count", get: (v) => v.share_count ?? 0 },
                { label: "Comment Count", get: (v) => v.comment_count ?? 0 },
                { label: "Tags", get: (v) => (v.keyword || []).join("; ") },
                { label: "Video URL", get: (v) => v.video_url },
                { label: "Thumbnail URL", get: (v) => v.thumb_url },
                { label: "Uploaded", get: (v) => v.created_at },
                { label: "ID", get: (v) => v._id },
              ]}
              request={async (page, limit) => {
                const res = await api.post("/admin/video_list", {
                  page,
                  limit,
                  search: debouncedSearch || undefined,
                  sortyBy,
                  sortDirection,
                })
                return {
                  docs: res.data?.data?.docs ?? [],
                  totalDocs: res.data?.data?.totalDocs ?? 0,
                }
              }}
            />
            <Button asChild>
              <Link to="/videos/new">Upload video</Link>
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="p-0">
          {videosQ.isError && (
            <div
              role="alert"
              className="m-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load videos. Try refreshing.</span>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Thumbnail</TableHead>
                <SortableTH field="narrated_by" label="Narrated By" {...{ onSortClick, SortIcon }} />
                <SortableTH field="author" label="Author" {...{ onSortClick, SortIcon }} />
                <SortableTH field="proverb" label="Proverb" {...{ onSortClick, SortIcon }} />
                <SortableTH field="purpose" label="Purpose" {...{ onSortClick, SortIcon }} />
                <SortableTH
                  field="login_user_sequence"
                  label="Login Seq"
                  className="w-28"
                  {...{ onSortClick, SortIcon }}
                />
                <SortableTH
                  field="skip_user_sequence"
                  label="Skip Seq"
                  className="w-28"
                  {...{ onSortClick, SortIcon }}
                />
                <SortableTH
                  field="like_count"
                  label={
                    <span className="flex items-center gap-1">
                      <ThumbsUp className="h-3.5 w-3.5" /> Likes
                    </span>
                  }
                  className="w-24"
                  {...{ onSortClick, SortIcon }}
                />
                <SortableTH field="created_at" label="Uploaded" {...{ onSortClick, SortIcon }} />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {videosQ.isLoading && (
                <TableRow>
                  <TableCell colSpan={10} className="h-32 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {!videosQ.isLoading && (videosQ.data?.docs.length ?? 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="h-32 text-center text-muted-foreground">
                    No videos match your filters.
                  </TableCell>
                </TableRow>
              )}
              {videosQ.data?.docs.map((v) => (
                <TableRow key={v._id}>
                  <TableCell>
                    {v.thumb_url ? (
                      <img
                        src={v.thumb_url}
                        alt=""
                        loading="lazy"
                        className="h-16 w-28 rounded object-cover"
                      />
                    ) : (
                      <div className="grid h-16 w-28 place-items-center rounded bg-muted text-xs text-muted-foreground">
                        no thumb
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-medium">
                    {v.narrated_by || "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {v.author || "—"}
                  </TableCell>
                  <TableCell className="max-w-[20rem] truncate text-muted-foreground">
                    {v.proverb || "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {v.purpose || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {typeof v.login_user_sequence === "number"
                      ? v.login_user_sequence
                      : "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {typeof v.skip_user_sequence === "number"
                      ? v.skip_user_sequence
                      : "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {typeof v.like_count === "number" ? v.like_count : 0}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatFullDate(v.created_at)}
                  </TableCell>
                  <TableCell className="space-x-1 text-right">
                    {v.video_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPlayVideo(v)}
                        aria-label={`Play ${v.narrated_by || v.author || "video"}`}
                      >
                        <Play className="h-4 w-4" />
                      </Button>
                    )}
                    <Button asChild variant="ghost" size="sm" aria-label="Edit video">
                      <Link to={`/videos/${v._id}`}>
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteVideo(v)}
                      aria-label="Delete video"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>

        {/* Pagination footer */}
        <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              Page {page}
              {totalPages > 0 ? ` of ${totalPages}` : ""}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={totalPages > 0 && page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Video player modal */}
      <Dialog
        open={!!playVideo}
        onOpenChange={(open) => !open && setPlayVideo(null)}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {playVideo?.narrated_by || playVideo?.author || "Video"}
            </DialogTitle>
            <DialogDescription>
              {playVideo?.proverb}
            </DialogDescription>
          </DialogHeader>
          {playVideo?.video_url ? (
            <video
              src={playVideo.video_url}
              controls
              autoPlay
              playsInline
              className="aspect-video w-full rounded bg-black"
            />
          ) : (
            <div className="grid h-48 place-items-center text-muted-foreground">
              Video URL missing.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlayVideo(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm modal */}
      <Dialog
        open={!!deleteVideo}
        onOpenChange={(open) => !open && setDeleteVideo(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete video?</DialogTitle>
            <DialogDescription>
              {deleteVideo?.narrated_by || deleteVideo?.author || "This video"}
              {" "}will be removed. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteVideo(null)}
              disabled={deleteMut.isPending}
            >
              Keep video
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete video
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function SortableTH({
  field,
  label,
  className,
  onSortClick,
  SortIcon,
}: {
  field: string
  label: React.ReactNode
  className?: string
  onSortClick: (field: string) => void
  SortIcon: React.ComponentType<{ field: string }>
}) {
  return (
    <TableHead className={cn("cursor-pointer select-none", className)}>
      <button
        type="button"
        onClick={() => onSortClick(field)}
        className="flex items-center gap-1 text-left font-medium text-muted-foreground hover:text-foreground"
      >
        {label}
        <SortIcon field={field} />
      </button>
    </TableHead>
  )
}
